import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  Divider,
  CircularProgress,
  Chip,
  Stack,
} from '@mui/material';
import {
  ContentPaste as PasteIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import type {
  MedicalReservationRecord,
  PasteStatusResponse,
} from '../../types/medicalReservation';

interface PasteConfirmDialogProps {
  open: boolean;
  selectedRecords: MedicalReservationRecord[];
  onClose: () => void;
  onConfirm: () => Promise<void>;
  pasteStatus?: PasteStatusResponse['data'] | null;
  isProcessing: boolean;
}

/**
 * 貼り付け確認ダイアログコンポーネント
 * 
 * 意図：
 * - 選択されたレコードの確認表示
 * - 医師ごとのグループ化表示
 * - 貼り付け処理の進捗表示
 * - 成功/エラー結果の表示
 * 
 * 注意点：
 * - 非同期処理中のUI状態管理
 * - エラー時の適切なフィードバック
 * - アクセシビリティ対応
 */
export const PasteConfirmDialog: React.FC<PasteConfirmDialogProps> = ({
  open,
  selectedRecords,
  onClose,
  onConfirm,
  pasteStatus,
  isProcessing,
}) => {
  const [groupedByDoctor, setGroupedByDoctor] = useState<Record<string, MedicalReservationRecord[]>>({});

  // 医師ごとにレコードをグループ化
  useEffect(() => {
    const grouped = selectedRecords.reduce((acc, record) => {
      const doctor = record.doctorInCharge;
      if (!acc[doctor]) {
        acc[doctor] = [];
      }
      acc[doctor].push(record);
      return acc;
    }, {} as Record<string, MedicalReservationRecord[]>);
    
    setGroupedByDoctor(grouped);
  }, [selectedRecords]);

  // ダイアログを閉じる処理
  const handleClose = () => {
    if (!isProcessing) {
      onClose();
    }
  };

  // 処理状態に応じたコンテンツを表示
  const renderContent = () => {
    // 処理完了時
    if (pasteStatus?.status === 'completed') {
      return (
        <Box>
          <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2 }}>
            貼り付け処理が完了しました！
          </Alert>
          
          <Typography variant="subtitle2" gutterBottom>
            処理結果:
          </Typography>
          
          <List dense>
            {pasteStatus.results?.map((result, index) => (
              <ListItem key={index}>
                <ListItemText
                  primary={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2">
                        {result.doctor_name}
                      </Typography>
                      {result.success ? (
                        <Chip
                          label="成功"
                          size="small"
                          color="success"
                          icon={<CheckCircleIcon />}
                        />
                      ) : (
                        <Chip
                          label="失敗"
                          size="small"
                          color="error"
                          icon={<ErrorIcon />}
                        />
                      )}
                    </Stack>
                  }
                  secondary={
                    result.success
                      ? `${result.record_count}件のデータを${result.sheet_name}シートに書き込みました`
                      : result.error || 'エラーが発生しました'
                  }
                />
              </ListItem>
            ))}
          </List>
        </Box>
      );
    }

    // エラー時
    if (pasteStatus?.status === 'failed') {
      return (
        <Alert severity="error" icon={<ErrorIcon />}>
          <Typography variant="body2" gutterBottom>
            貼り付け処理中にエラーが発生しました
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {pasteStatus.error_message || '不明なエラーが発生しました'}
          </Typography>
        </Alert>
      );
    }

    // 処理中
    if (isProcessing && pasteStatus) {
      return (
        <Box>
          <Box sx={{ mb: 3 }}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">
                {pasteStatus.message || '処理中...'}
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={pasteStatus.progress}
              sx={{ height: 8, borderRadius: 4 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              {pasteStatus.completed_count} / {pasteStatus.total_count} 件処理済み
            </Typography>
          </Box>
          
          {pasteStatus.message && (
            <Alert severity="info" icon={<CircularProgress size={20} />}>
              {pasteStatus.message}
            </Alert>
          )}
        </Box>
      );
    }

    // 確認画面（初期状態）
    return (
      <Box>
        <Alert severity="info" icon={<WarningIcon />} sx={{ mb: 3 }}>
          選択された{selectedRecords.length}件の予約データを、担当医師のGoogleスプレッドシートに貼り付けます。
        </Alert>

        <Typography variant="subtitle2" gutterBottom>
          医師別の内訳:
        </Typography>
        
        <List dense>
          {Object.entries(groupedByDoctor).map(([doctor, records]) => (
            <ListItem key={doctor}>
              <ListItemText
                primary={doctor}
                secondary={`${records.length}件のデータ`}
              />
            </ListItem>
          ))}
        </List>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" gutterBottom>
          選択されたデータ:
        </Typography>
        
        <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
          <List dense>
            {selectedRecords.map((record) => (
              <ListItem key={record.id}>
                <ListItemText
                  primary={`${record.patientName} - ${record.anybotId}`}
                  secondary={`担当: ${record.doctorInCharge} | 診療開始: ${new Date(record.consultationStartTime).toLocaleString('ja-JP')}`}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={isProcessing}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <PasteIcon />
          スプレッドシートへの貼り付け
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        {renderContent()}
      </DialogContent>
      
      <DialogActions>
        {!isProcessing && (
          <Button onClick={handleClose} color="inherit">
            {pasteStatus?.status === 'completed' || pasteStatus?.status === 'failed' ? '閉じる' : 'キャンセル'}
          </Button>
        )}
        
        {!pasteStatus && !isProcessing && (
          <Button
            onClick={onConfirm}
            variant="contained"
            startIcon={<PasteIcon />}
            disabled={
              isProcessing ||
              selectedRecords.length === 0
            }
          >
            貼り付けを実行
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};